import { initializeApp } from 'firebase/app';
import { getMessaging, getToken, onMessage, isSupported } from 'firebase/messaging';

// Firebase configuration
// Bu değerleri environment variables'dan almalısınız
const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY || "YOUR_API_KEY",
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN || "YOUR_AUTH_DOMAIN",
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID || "YOUR_PROJECT_ID",
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET || "YOUR_STORAGE_BUCKET",
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID || "YOUR_SENDER_ID",
  appId: import.meta.env.VITE_FIREBASE_APP_ID || "YOUR_APP_ID",
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID || "YOUR_MEASUREMENT_ID"
};

// VAPID key - Firebase Console > Project Settings > Cloud Messaging > Web Push certificates
const vapidKey = import.meta.env.VITE_FIREBASE_VAPID_KEY || "YOUR_VAPID_KEY";

// Initialize Firebase
const app = initializeApp(firebaseConfig);

let messaging = null;

// Initialize messaging only in browser and if supported
const initMessaging = async () => {
  if (typeof window === 'undefined') return null;
  
  try {
    const isSupportedBrowser = await isSupported();
    if (!isSupportedBrowser) {
      console.warn('Firebase Messaging is not supported in this browser');
      return null;
    }
    
    messaging = getMessaging(app);
    return messaging;
  } catch (error) {
    console.error('Error initializing Firebase Messaging:', error);
    return null;
  }
};

// Request notification permission and get FCM token
const requestNotificationPermission = async () => {
  try {
    if (!('Notification' in window)) {
      console.warn('This browser does not support notifications');
      return null;
    }

    if (Notification.permission === 'granted') {
      return await getFCMToken();
    }

    if (Notification.permission === 'default') {
      const permission = await Notification.requestPermission();
      if (permission === 'granted') {
        return await getFCMToken();
      }
    }

    console.warn('Notification permission denied');
    return null;
  } catch (error) {
    console.error('Error requesting notification permission:', error);
    return null;
  }
};

// Register service worker and clean up duplicates
const registerServiceWorker = async () => {
  if (!('serviceWorker' in navigator)) {
    console.warn('Service Worker not supported');
    return null;
  }

  try {
    // Get all existing service worker registrations
    const existingRegistrations = await navigator.serviceWorker.getRegistrations();
    
    // Target service worker file
    const targetScript = '/firebase-messaging-sw.js';
    const targetScope = window.location.origin + '/';
    
    // iOS'ta çift kayıt önleme: Önce mevcut kayıtları kontrol et
    for (const registration of existingRegistrations) {
      // Get script URL from active, waiting, or installing worker
      const scriptURL = registration.active?.scriptURL || 
                       registration.waiting?.scriptURL || 
                       registration.installing?.scriptURL || 
                       '';
      
      const fullScriptURL = scriptURL ? new URL(scriptURL, window.location.origin).pathname : '';
      
      // Eğer aynı script ve scope'a sahip bir service worker varsa, onu kullan
      if (fullScriptURL === targetScript && registration.scope === targetScope) {
        console.log('Service worker already registered, reusing existing:', registration);
        // Güncellemeyi dene ama beklemeden devam et
        registration.update().catch(err => {
          // Update hatası normal (yeni versiyon yoksa)
          console.log('Service worker update check completed');
        });
        return registration;
      }
      
      // If it's not our target service worker, unregister it
      if (scriptURL && !scriptURL.includes('firebase-messaging-sw.js')) {
        console.log('Unregistering duplicate service worker:', scriptURL);
        try {
          await registration.unregister();
          console.log('Successfully unregistered duplicate service worker');
        } catch (error) {
          console.warn('Error unregistering service worker:', error);
        }
      }
      // If it's our target service worker but wrong scope, unregister it
      else if (scriptURL.includes('firebase-messaging-sw.js') && registration.scope !== targetScope) {
        console.log('Unregistering service worker with wrong scope:', registration.scope);
        try {
          await registration.unregister();
        } catch (error) {
          console.warn('Error unregistering service worker:', error);
        }
      }
    }

    // Eğer mevcut kayıt yoksa, yeni kayıt oluştur
    const registration = await navigator.serviceWorker.register(targetScript, {
      scope: '/'
    });
    
    console.log('Service Worker registered:', registration);
    
    // Check for waiting service worker and prompt update if needed
    if (registration.waiting) {
      console.log('New service worker waiting, consider updating');
    }
    
    return registration;
  } catch (error) {
    console.error('Service Worker registration failed:', error);
    return null;
  }
};

// Get FCM token
const getFCMToken = async () => {
  try {
    if (!messaging) {
      messaging = await initMessaging();
      if (!messaging) return null;
    }

    if (!vapidKey || vapidKey === "YOUR_VAPID_KEY") {
      console.error('VAPID key is not configured');
      return null;
    }

    // Register service worker first
    await registerServiceWorker();

    const token = await getToken(messaging, { vapidKey });
    
    if (token) {
      console.log('FCM Token:', token);
      // Store token in localStorage for later use
      localStorage.setItem('fcm_token', token);
      return token;
    } else {
      console.warn('No registration token available');
      return null;
    }
  } catch (error) {
    console.error('An error occurred while retrieving token:', error);
    return null;
  }
};

// Check if app is in foreground/active
const isAppInForeground = () => {
  if (typeof document === 'undefined') return false;
  return document.visibilityState === 'visible';
};

// Handle foreground messages - returns cleanup function
const setupForegroundMessageListener = (callback) => {
  let unsubscribe = null;

  const setupListener = async () => {
    if (!messaging) {
      messaging = await initMessaging();
      if (!messaging) return null;
    }

    unsubscribe = onMessage(messaging, (payload) => {
      console.log('Message received in foreground:', payload);
      
      // Sadece app gerçekten foreground'daysa göster
      if (!isAppInForeground()) {
        console.log('App not in foreground, skipping foreground notification');
        return;
      }
      
      if (callback) {
        callback(payload);
      }
    });

    return unsubscribe;
  };

  setupListener().catch(error => {
    console.error('Error setting up foreground message listener:', error);
  });

  // Return cleanup function
  return () => {
    if (unsubscribe) {
      unsubscribe();
    }
  };
};

// Get or generate device ID
const getDeviceId = () => {
  let deviceId = localStorage.getItem('device_id');
  if (!deviceId) {
    deviceId = 'web_' + Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
    localStorage.setItem('device_id', deviceId);
  }
  return deviceId;
};

// Get device name
const getDeviceName = () => {
  return navigator.userAgent || 'Unknown Browser';
};

// Get platform
const getPlatform = () => {
  return 'web';
};

export {
  app,
  initMessaging,
  requestNotificationPermission,
  getFCMToken,
  setupForegroundMessageListener,
  getDeviceId,
  getDeviceName,
  getPlatform
};

